import { useEffect, useState } from 'react';
import { Alert, Box, Card } from '@mui/material';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { TransactionsDetailedResponse } from '@/models/transaction';
import {
    CompanyPaymentDetailsResponse,
    GetAiiaAuthorizeUrlResponse
} from '@/models/payment';
import { TransactionsApprovalTable } from './TransactionsApprovalTable';
import { useErrorHandling } from '@/utils/errorHandling';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import { useAppDispatch } from '@/lib/hooks';
import { useTranslation } from 'react-i18next';
import CardHeader from '@/components/shared/CardHeader';
import Chip from '@/components/shared/Chip';
import { TablePagination } from '@/components/shared/TablePagination';
import { ApproveButton, RejectButton } from '@/components/shared/Button';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useUser } from '@/contexts/user/UserContext';
import {
    useCurrentCompanyDetails,
    useGetPendingTransactions
} from '@/services/paymentsQuery';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { handleDates } from '@/services/utils';

export enum TransactionListType {
    AwaitingManager = 'AwaitingManager',
    AwaitingBank = 'AwaitingBank'
}

interface TransactionsListProps {
    type: TransactionListType;
}

export const TransactionsList = ({ type }: TransactionsListProps) => {
    const { t } = useTranslation();
    const [transactions, setTransactions] =
        useState<TransactionsDetailedResponse>();
    const [companyPaymentDetails, setCompanyPaymentDetails] =
        useState<CompanyPaymentDetailsResponse>();
    const [checked, setChecked] = useState<
        { transactionId: string; transactionStatus: string }[]
    >([]);
    const [errorMessage, setErrorMessage] = useState<string>();
    const { setAlert } = useNotificationsSnackbar();
    const dispatch = useAppDispatch();
    const { backendErrorHandler } = useErrorHandling();
    const { user } = useUser();

    const { data: company } = useCurrentCompanyDetails();

    useEffect(() => {
        if (company?.data) {
            setCompanyPaymentDetails(
                company.data as unknown as CompanyPaymentDetailsResponse
            );
        }
    }, [company]);

    const {
        data: transactionsResponse,
        isPending,
        error,
        isError,
        refetch: loadData
    } = useGetPendingTransactions();

    useEffect(() => {
        if (transactionsResponse?.data) {
            let transactions: TransactionsDetailedResponse = JSON.parse(
                JSON.stringify(transactionsResponse.data)
            );
            handleDates(transactions);
            transactions.transactions = transactions?.transactions?.filter(
                (transaction) => {
                    if (type === TransactionListType.AwaitingManager) {
                        return transaction.status === 'PendingApproval';
                    } else if (type === TransactionListType.AwaitingBank) {
                        return transaction.status === 'AwaitingBank';
                    }
                    return false;
                }
            );

            setTransactions(transactions);
            setChecked([]);
        }
    }, [transactionsResponse]);

    useEffect(() => {
        dispatch(setLoader(isPending));
    }, [isPending]);

    useEffect(() => {
        if (isError) {
            setErrorMessage(
                'Could not get transactions list: ' + error.message
            );
        }
    }, [isError, error]);

    const handleToggle = (transactionId: string, transactionStatus: string) => {
        const currentIndex = checked.findIndex(
            (item) => item.transactionId === transactionId
        );
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push({ transactionId, transactionStatus });
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const handleToggleAll = () => () => {
        const allChecked =
            checked.length === transactions?.transactions?.length;
        let newChecked: { transactionId: string; transactionStatus: any }[] =
            [];

        if (allChecked) {
            newChecked = [];
        } else {
            newChecked =
                transactions?.transactions?.map((transaction) => ({
                    transactionId: transaction.transactionId,
                    transactionStatus: transaction.status
                })) ?? [];
        }

        setChecked(newChecked);
    };

    const pendingApprovalTransactions = checked.filter(
        (item) => item.transactionStatus === 'PendingApproval'
    );

    const succeedFailTransactions = checked.filter(
        (item) => item.transactionStatus === 'AwaitingBank'
    );

    const handleAiiaWithdrawal = async () => {
        if (
            companyPaymentDetails?.aiiaPaymentsAllowedPerAuthorization &&
            pendingApprovalTransactions.length >
                companyPaymentDetails?.aiiaPaymentsAllowedPerAuthorization
        ) {
            setAlert(
                `You cannot authorize more than ${companyPaymentDetails?.aiiaPaymentsAllowedPerAuthorization} payments in one batch`,
                'error'
            );
            return;
        }

        dispatch(setLoader(true));
        PaymentsAPIServiceV2.getInstance()
            .getAiiaAuthorizeUrl({
                transactionIds: pendingApprovalTransactions.map(
                    (item) => item.transactionId
                )
            })
            .then((response: any) => {
                let responseBody: GetAiiaAuthorizeUrlResponse = response.data;
                window.location.href = responseBody.authorizeUrl;
            })
            .catch(
                backendErrorHandler(
                    'Could not authroize transactions list: ',
                    () => {
                        loadData();
                    }
                )
            )
            .finally(() => {
                dispatch(setLoader(false));
            });
    };

    const {
        rejectTransactions,
        approveTransactions,
        succeedTransactionInSalarySystem,
        failTransactionInSalarySystem
    } = usePaymentsMutations();

    useEffect(() => {
        if (approveTransactions.isSuccess) {
            setAlert('Transactions approved successfully', 'success');
            loadData();
        }
    }, [approveTransactions.isSuccess]);

    useEffect(() => {
        if (approveTransactions.isError) {
            backendErrorHandler('Could not approve transactions: ')(
                approveTransactions.error
            );
            loadData();
        }
    }, [approveTransactions.isError]);

    const handleDanskeBankWithdrawal = async () => {
        approveTransactions.mutate({
            transactionIds: pendingApprovalTransactions.map(
                (item) => item.transactionId
            )
        });
    };

    useEffect(() => {
        if (failTransactionInSalarySystem.isSuccess) {
            setAlert('Transactions failed successfully', 'success');
            loadData();
        }
    }, [failTransactionInSalarySystem.isSuccess]);

    useEffect(() => {
        if (failTransactionInSalarySystem.isError) {
            backendErrorHandler('Could not fail transactions list: ')(
                failTransactionInSalarySystem.error
            );
            loadData();
        }
    }, [failTransactionInSalarySystem.isError]);

    const handleTransactionFail = async (
        e: React.SyntheticEvent
    ): Promise<void> => {
        e.preventDefault();

        failTransactionInSalarySystem.mutate({
            transactionIds: succeedFailTransactions.map(
                (item) => item.transactionId
            )
        });
    };

    useEffect(() => {
        if (succeedTransactionInSalarySystem.isSuccess) {
            setAlert('Transactions succeeded', 'success');
            loadData();
        }
    }, [succeedTransactionInSalarySystem.isSuccess]);

    useEffect(() => {
        if (succeedTransactionInSalarySystem.isError) {
            backendErrorHandler('Could not succeed transactions list: ')(
                succeedTransactionInSalarySystem.error
            );
            loadData();
        }
    }, [succeedTransactionInSalarySystem.isError]);

    const handleTransactionSucceed = async (
        e: React.SyntheticEvent
    ): Promise<void> => {
        e.preventDefault();

        succeedTransactionInSalarySystem.mutate({
            transactionIds: succeedFailTransactions.map(
                (item) => item.transactionId
            )
        });
    };

    const onApproveWithdrawals = async (
        e: React.SyntheticEvent
    ): Promise<void> => {
        e.preventDefault();

        if (companyPaymentDetails?.paymentProvider === 'Aiia') {
            handleAiiaWithdrawal();
        } else if (companyPaymentDetails?.paymentProvider === 'DanskeBank') {
            handleDanskeBankWithdrawal();
        } else if (
            companyPaymentDetails?.paymentSource === 'InstaPaidAccount' ||
            companyPaymentDetails?.paymentSource === 'InstaPaidEurAccount'
        ) {
            handleDanskeBankWithdrawal();
        } else {
            setAlert(
                'Unknown payment provider - please contact support',
                'warning'
            );
        }
    };

    useEffect(() => {
        if (rejectTransactions.isSuccess) {
            setAlert('Withdrawal request(-s) rejected successfully', 'success');
            loadData();
        }
    }, [rejectTransactions.isSuccess]);

    useEffect(() => {
        if (rejectTransactions.isError) {
            backendErrorHandler('Could not reject transactions: ')(
                rejectTransactions.error
            );
        }
    }, [rejectTransactions.isError]);

    const onRejectWithdrawals = async (
        e: React.SyntheticEvent
    ): Promise<void> => {
        e.preventDefault();
        rejectTransactions.mutate({
            transactionIds: pendingApprovalTransactions.map(
                (item) => item.transactionId
            )
        });
    };

    const shouldShowApproveReject = checked.some(
        (item) => item?.transactionStatus === 'PendingApproval'
    );
    const shouldShowSucceedFail = checked.some(
        (item) => item.transactionStatus === 'AwaitingBank'
    );

    return (
        <Box>
            <Card>
                <CardHeader
                    label={t('transactions')}
                    chip={
                        <Chip
                            icon={<span></span>}
                            label={transactions?.transactions.length}
                            color={'success'}
                        />
                    }
                />
                {transactions && (
                    <>
                        {transactions.transactions.length === 0 && (
                            <Alert severity="info">
                                {t('pending_transactions_placeholder')}
                            </Alert>
                        )}
                        {transactions.transactions.length > 0 && (
                            <>
                                <TransactionsApprovalTable
                                    checked={checked}
                                    transactions={transactions.transactions}
                                    handleToggle={handleToggle}
                                    handleAllToggle={handleToggleAll}
                                />

                                {companyPaymentDetails?.paymentProvider ===
                                    'Aiia' && (
                                    <Alert severity="info">
                                        Your bank supports approval of a total
                                        of{' '}
                                        {
                                            companyPaymentDetails?.aiiaPaymentsAllowedPerAuthorization
                                        }{' '}
                                        payment(-s) in a batch
                                    </Alert>
                                )}
                            </>
                        )}
                    </>
                )}
                <TablePagination
                    count={0}
                    page={0}
                    onPageChange={() => {}}
                    rowsPerPage={-1}
                    onRowsPerPageChange={() => {}} //TODO: Fix this
                    rowsPerPageOptions={[
                        {
                            label: 'All',
                            value: -1
                        }
                    ]}
                    actions={
                        <Box>
                            {type == TransactionListType.AwaitingManager && (
                                <>
                                    <RejectButton
                                        disabled={
                                            approveTransactions.isPending ||
                                            checked.length === 0
                                        }
                                        loading={rejectTransactions.isPending}
                                        onClick={onRejectWithdrawals}
                                        style={{ marginRight: 4 }}
                                    >
                                        Reject
                                    </RejectButton>
                                    <ApproveButton
                                        disabled={
                                            rejectTransactions.isPending ||
                                            checked.length === 0
                                        }
                                        loading={approveTransactions.isPending}
                                        onClick={onApproveWithdrawals}
                                    >
                                        Approve
                                    </ApproveButton>
                                </>
                            )}
                            {type == TransactionListType.AwaitingBank &&
                                user?.role === 'SuperAdmin' && (
                                    <>
                                        <RejectButton
                                            style={{ marginRight: 4 }}
                                            disabled={
                                                succeedTransactionInSalarySystem.isPending ||
                                                checked.length === 0
                                            }
                                            loading={
                                                failTransactionInSalarySystem.isPending
                                            }
                                            onClick={handleTransactionFail}
                                        >
                                            Failed
                                        </RejectButton>
                                        <ApproveButton
                                            disabled={
                                                failTransactionInSalarySystem.isPending ||
                                                checked.length === 0
                                            }
                                            loading={
                                                succeedTransactionInSalarySystem.isPending
                                            }
                                            onClick={handleTransactionSucceed}
                                        >
                                            Succeed
                                        </ApproveButton>
                                    </>
                                )}
                        </Box>
                    }
                />
            </Card>

            {/* TODO: error UI change */}
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Box>
    );
};
